import type { ReactNode } from 'react';
import { Stack } from '@mui/material';
import LoadingButton from '@shared/components/buttons/LoadingButton/LoadingButton';
import { SelectableCard } from '@shared/components/SelectableCard';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import PhoneIcon from '@assets/icons/phoneIcon-alt.svg?component';
import TvIcon from '@assets/icons/tvIcon-alt.svg?component';
import WifiIcon from '@assets/icons/wifiIcon.svg?component';
import type { CategoryType, Plan } from '@t/productOffering.types';
import { PlanHighlights } from './PlanHighlights';
import { PriceSection } from './PriceSection';

interface PlanCardProps {
  plan: Plan;
  isSelected: boolean;
  isSelectDisabled?: boolean;
  onSelectPlan: () => void;
  onEdit?: () => void;
  onViewDetails: () => void;
  categoryType: CategoryType;
  isSinglePlan?: boolean;
  loading?: boolean;
  children?: ReactNode;
}

const CategoryIcons: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  Internet: WifiIcon,
  Video: TvIcon,
  Phone: PhoneIcon,
};

const CategoryIcon = ({ categoryType }: { categoryType?: CategoryType }) => {
  const IconComponent = (categoryType && CategoryIcons[categoryType]) || WifiIcon;

  return <SvgIcon component={IconComponent} inheritViewBox className="PlanCard-categoryIcon" />;
};

export const PlanCard = ({
  plan,
  isSelected,
  isSelectDisabled,
  onSelectPlan,
  onEdit,
  onViewDetails,
  categoryType,
  isSinglePlan,
  loading,
  children,
}: PlanCardProps) => {
  const { name, price, priceUnavailable, priceType, oneTimeFee, description } = plan;
  const { summary, highlights, discountTag, discountMessage } = description;

  const cardVariant = isSinglePlan ? 'planCardSingle' : 'planCard';
  const buttonVariant = isSelected ? 'editPlan' : 'contained';
  const buttonLabel = isSelected ? 'EDIT PLAN' : 'SELECT PLAN';
  const onPrimaryAction = isSelected && onEdit ? onEdit : onSelectPlan;

  const priceSuffix = priceType === 'oneTime' ? 'one-time' : '/ month';
  const priceSection = (
    <PriceSection
      price={price}
      priceUnavailable={priceUnavailable}
      priceSuffix={priceSuffix}
      discountTag={discountTag}
      discountMessage={discountMessage}
      oneTimeFee={oneTimeFee}
    />
  );
  const primaryButton = (
    <LoadingButton variant={buttonVariant} onClick={onPrimaryAction} loading={loading} disabled={isSelectDisabled && !isSelected}>
      {buttonLabel}
    </LoadingButton>
  );

  return (
    // No onSelect: the SELECT PLAN button is the activation surface, so the card stays
    // presentation-only (no role/tabIndex). `selected` drives the variant's selected border.
    <SelectableCard selectionMode="single" selected={isSelected}>
      {(selectable) => (
        // `PlanCard--<CategoryType>` lets the theme's planCard variants apply the
        // tenant's per-category selected border and badge colour (CPQ Branding ->
        // Plan Card) without this component reading the theme. SelectableCardRenderProps
        // carries no className, so the spread cannot clobber it.
        <Card
          variant={cardVariant}
          selected={isSelected}
          elevation={0}
          {...selectable}
          className={categoryType ? `PlanCard--${categoryType}` : undefined}
        >
          <Stack className="PlanCard-info" gap="20px" justifyContent="center">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" gap="12px" alignItems="center">
                <CategoryIcon categoryType={categoryType} />
                <Typography variant="title.plan" className="PlanCard-title">
                  {name}
                </Typography>
              </Stack>
              {isSelected && (
                <Chip
                  variant="planSelectedBadge"
                  label="Selected"
                  size="small"
                  className={categoryType ? `PlanCard--${categoryType}` : undefined}
                />
              )}
            </Stack>
            {children}
            {!isSinglePlan && priceSection}
            {/* Single-plan cards keep the price in the right-hand pricing column on
                desktop, but on mobile the columns stack and the price must sit under
                the title like multi-plan cards. Render it here too; the theme shows
                this copy only below the tablet breakpoint. */}
            {isSinglePlan && <Box className="PlanCard-infoPrice">{priceSection}</Box>}
            {summary && <Typography variant="body.sm">{summary}</Typography>}
          </Stack>

          {isSinglePlan && <Divider orientation="vertical" flexItem variant="planSection" />}

          <Stack className="PlanCard-highlights" gap="20px">
            <PlanHighlights highlights={highlights} />
            <Box component="button" className="PlanCard-detailsLink" onClick={onViewDetails}>
              View Plan Details
            </Box>
          </Stack>

          {isSinglePlan && <Divider orientation="vertical" flexItem variant="planSection" />}

          {isSinglePlan ? (
            <Stack className="PlanCard-pricing" gap="20px" justifyContent="center">
              <Box className="PlanCard-pricingPrice">{priceSection}</Box>
              {primaryButton}
            </Stack>
          ) : (
            primaryButton
          )}
        </Card>
      )}
    </SelectableCard>
  );
};
